<template>
  <div class="container">
    <!-- Screenshot Header (only visible during screenshot capture) -->
    <div class="screenshot-header" :class="{ hidden: !showScreenshotHeader }">
      <div class="maze-pattern">
        <div class="header-content">
          <div class="workspace-info">
            <img
              v-if="currentWorkspaceLogo"
              :src="currentWorkspaceLogo"
              alt="Workspace Logo"
              class="workspace-logo"
            />
            <span class="workspace-name">{{
              currentWorkspaceName || "Workspace"
            }}</span>
          </div>
          <div class="date-text">{{ currentDate }}</div>
        </div>
      </div>
    </div>

    <!-- Title with Back Arrow -->
    <!-- <h1 class="reviews-title">
      <v-icon @click="goBack" class="back-arrow">mdi-arrow-left</v-icon>
      
      Latest Review
    </h1> -->

    <!-- Loader -->
    <div v-if="isLoading" class="loader">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p>Loading...</p>
    </div>

    <!-- Toggle Buttons -->
    <div  v-if="!showScreenshotHeader" class="filter-buttons">
      <div class="d-flex align-center w-100">
        <v-select
          v-model="selectedFilter"
          :items="filterOptions"
          label="Filter Locations"
          outlined
          dense
          @change="applyFilter"
          class="mr-2 filter-select"
          hide-details
        ></v-select>
        <v-btn
          color="primary"
          icon
          @click="captureScreenshot"
          title="Save page as image"
        >
          <v-icon>mdi-download-box-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="search-bar">
      <input
        type="text"
        v-model="searchTerm"
        placeholder="Search locations..."
        @input="filterLocations"
      />
    </div>

    <div
      v-if="filteredLocations.length === 0 && !isLoading"
      class="text-center py-8"
    >
      <div class="p-8 bg-white rounded-lg shadow-sm max-w-3xl mx-auto">
        <p class="text-gray-500 mb-4">
          No locations found. Please check your filters or connect Google
          Business locations.
        </p>
      </div>
    </div>
    <div v-else>
      <table class="locations-table">
        <thead>
          <tr>
            <th>Location</th>
            <th>Area</th>
            <th>City</th>
            <th>State, Zip, Country</th>
            <th
              class="sortable-header"
              @click="sortBy('lastReviewUpdateTime')"
              :class="{ sorted: sortColumn === 'lastReviewUpdateTime' }"
            >
              Last Received Review
              <v-icon
                small
                :color="
                  sortColumn === 'lastReviewUpdateTime' ? 'white' : 'white'
                "
              >
                {{ getSortIcon("lastReviewUpdateTime") }}
              </v-icon>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="location in filteredAndSearchedLocations.slice(
              (currentPage - 1) * itemsPerPage,
              currentPage * itemsPerPage
            )"
            :key="location.id"
          >
            <td>{{ location.storeLocation }}</td>
            <td>{{ location.area }}</td>
            <td>{{ location.city }}</td>
            <td>{{ location.stateZipCountry }}</td>
            <td>
              <span v-if="location.lastReviewUpdateTime === 'N/A'">
                N/A
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="primary"
                      small
                      v-bind="attrs"
                      v-on="on"
                      class="info-icon"
                    >
                      mdi-information
                    </v-icon>
                  </template>
                  <span>No reviews received since connection with Fly</span>
                </v-tooltip>
              </span>
              <span v-else>{{ location.lastReviewUpdateTime }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination Controls -->
      <div class="pagination">
        <div class="pagination-controls">
          <button
            class="pagination-button"
            @click="prevPage"
            :disabled="currentPage === 1"
          >
            Previous
          </button>
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <button
            class="pagination-button"
            @click="nextPage"
            :disabled="currentPage === totalPages"
          >
            Next
          </button>
        </div>
        <div  v-if="!showScreenshotHeader" class="items-per-page">
          <v-select
            v-model="itemsPerPage"
            :items="itemsPerPageOptions"
            label="Items per page"
            outlined
            dense
            hide-details
            class="items-per-page-select"
            @change="handleItemsPerPageChange"
          ></v-select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from "@/utils/firebase.utils"; // Adjust the import based on your project structure
import {
  collection,
  onSnapshot,
  where,
  query,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import EmptyState from "@/components/ui/common/EmptyState.vue";
import connectedAppsMixins from "@/mixins/connected.apps.mixins";
import html2canvas from "html2canvas"; // Add this import

export default {
  components: {
    EmptyState,
  },
  mixins: [connectedAppsMixins],
  data() {
    return {
      showScreenshotHeader: false,
      workspaceData: null,
      locations: [], // Array to hold fetched locations
      filteredLocations: [], // Array to hold filtered locations
      noReview: [], // Array to hold locations without reviews
      noReviewLastWeek: [], // Array to hold locations without reviews for past week
      selectedFilter: "noReview", // Default filter
      filterOptions: [
        { text: "Locations without Reviews", value: "noReview" },
        { text: "Unreviewed Last Week", value: "unreviewedLastWeek" },
        { text: "All Locations", value: "all" },
      ],
      isLoading: false, // Loading state
      currentPage: 1, // Current page for pagination
      itemsPerPage: 10, // Default number of items per page
      itemsPerPageOptions: [
        { text: "10", value: 10 },
        { text: "20", value: 20 },
        { text: "50", value: 50 },
        { text: "100", value: 100 },
      ],
      searchTerm: "", // New data property for search term
      sortColumn: null, // Column to sort by
      sortDirection: "asc", // Sort direction: 'asc' or 'desc'
    };
  },
  computed: {
    currentWorkspace() {
      return this.$store.getters.currentWorkspace;
    },
    currentWorkspaceName() {
      return (
        this.workspaceData?.name || this.currentWorkspace?.name || "Workspace"
      );
    },
    currentWorkspaceLogo() {
      return (
        this.workspaceData?.logo_original ||
        this.currentWorkspace?.logo_original
      );
    },
    currentDate() {
      const now = new Date();
      return now.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });
    },
    totalPages() {
      return Math.ceil(this.filteredLocations.length / this.itemsPerPage);
    },
    paginatedLocations() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredLocations.slice(start, end);
    },
    filteredAndSearchedLocations() {
      let result = this.filteredLocations.filter((location) =>
        location.storeLocation
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase())
      );
      // Apply sorting if a column is selected
      if (this.sortColumn) {
        result = this.sortLocations(result);
      }

      return result;
    },
  },
  methods: {
    async fetchWorkspaceData() {
      if (!this.WID || !this.currentUID) {
        console.warn("Cannot fetch workspace data: missing WID or currentUID");
        return;
      }

      try {
        const workspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID
        );
        const workspaceDoc = await getDoc(workspaceRef);

        if (workspaceDoc.exists()) {
          this.workspaceData = workspaceDoc.data();
        } else {
          console.warn("Workspace not found in Firestore for WID:", this.WID);
        }
      } catch (error) {
        console.error("Error fetching workspace data:", error);
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    applyFilter() {
      this.searchTerm = ""; // Reset search term
      this.currentPage = 1; // Reset to first page
      if (this.selectedFilter === "noReview") {
        this.filteredLocations = this.noReview;
      } else if (this.selectedFilter === "unreviewedLastWeek") {
        this.filteredLocations = this.noReviewLastWeek;
      } else {
        this.filteredLocations = this.locations; // 'all'
      }
    },
    async fetchLocations() {
      const uid = this.currentUID; // Get the current user ID
      const locationsRef = collection(db, "users", uid, "fly_workspaces");
      const locationQuery = query(
        locationsRef,
        where("admin", "==", this.WID),
        where("storeWorkspace", "==", true)
      );
      const querySnapshot = await getDocs(locationQuery);
      // Listen for real-time updates
      this.locations = []; // Clear existing locations
      this.noReview = []; // Clear existing noReview array
      this.noReviewLastWeek = []; // Clear existing noReviewLastWeek array

      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // Calculate one week ago

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        // console.log('Fetched document data:', JSON.stringify(docData, null, 2)); // Log the document data, removed as it's no longer needed
        let lastReviewUpdateTime = "N/A"; // Default value
        let noReviewLastWeek = false;
        // Check conditions
        if (
          (docData.storeWorkspace &&
            docData.admin === this.WID &&
            docData.connected_apps?.includes("google_business_profile")) ||
          docData.shallowConnect
        ) {
          // Check lastReviewUpdateTime
          if (docData.lastReviewUpdateTime) {
            const reviewTime = docData.lastReviewUpdateTime.toDate(); // Convert Firestore timestamp to Date
            lastReviewUpdateTime = reviewTime.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }); // Format date
            if (reviewTime <= oneWeekAgo) {
              noReviewLastWeek = true;
            }
          }

          // Add document ID and data to the locations array
          const location = {
            id: doc.id,
            storeLocation: docData.storeLocation,
            area:
              docData.gmbProfile?.storefrontAddress?.addressLines?.[0]
                ?.split(",")[0]
                ?.trim() || "N/A",
            city: docData.gmbProfile?.storefrontAddress?.locality || "N/A",
            stateZipCountry: `${
              docData.gmbProfile?.storefrontAddress?.administrativeArea || "N/A"
            }, ${docData.gmbProfile?.storefrontAddress?.postalCode || "N/A"}, ${
              docData.gmbProfile?.storefrontAddress?.regionCode || "N/A"
            }`,
            lastReviewUpdateTime,
          };
          this.locations.push(location);

          // Populate noReview array
          if (lastReviewUpdateTime === "N/A") {
            this.noReview.push(location);
          }

          if (noReviewLastWeek || lastReviewUpdateTime === "N/A") {
            this.noReviewLastWeek.push(location);
          }
        }
      });
      this.filteredLocations = this.locations;
      this.applyFilter(); // Apply initial filter based on selectedFilter
      this.currentPage = 1; // Reset to first page when data changes
    },
    goBack() {
      this.$router.push("/gmb-reviews"); // Navigate back to the specified route
    },
    filterLocations() {
      this.currentPage = 1; // Reset to first page when filtering
    },
    async captureScreenshot() {
      try {
        // Show a loading indicator
        this.$store.commit("showSnackbar", {
          text: "Preparing screenshot...",
          color: "info",
        });

        // Show header for screenshot
        this.showScreenshotHeader = true;

        // Wait for DOM update
        await this.$nextTick();

        // Get the container element to capture
        const containerElement = document.querySelector(".container");
        if (!containerElement) {
          throw new Error("Container element not found");
        }

        // Use html2canvas to capture the element
        const canvas = await html2canvas(containerElement, {
          scale: 2, // Higher quality
          useCORS: true, // Allow cross-origin images
          logging: false,
          backgroundColor: "#ffffff",
        });

        // Hide header after screenshot
        this.showScreenshotHeader = false;

        // Convert to image and download
        const imageData = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = imageData;

        // Generate filename with current date
        const date = new Date();
        const formattedDate = date.toISOString().split("T")[0];
        link.download = `${this.currentWorkspaceName}-no-review-locations-${formattedDate}.png`;

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Success message
        this.$snackbar.show("Screenshot saved");
      } catch (error) {
        console.error("Error capturing screenshot:", error);
        this.showScreenshotHeader = false; // Ensure header is hidden on error
        this.$snackbar.show("Failed to save screenshot");
      }
    },
    handleItemsPerPageChange() {
      this.currentPage = 1; // Reset to first page when changing items per page
      // Scroll to the top of the table
      const tableElement = document.querySelector(".locations-table");
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        // Toggle sort direction if same column is clicked
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        // Set new column and default to ascending
        this.sortColumn = column;
        this.sortDirection = "asc";
      }
      this.currentPage = 1; // Reset to first page when sorting
    },

    sortLocations(locations) {
      if (this.sortColumn === "lastReviewUpdateTime") {
        return locations.sort((a, b) => {
          const aValue = a.lastReviewUpdateTime;
          const bValue = b.lastReviewUpdateTime;

          // Handle N/A values - put them at the end for ascending, beginning for descending
          if (aValue === "N/A" && bValue === "N/A") return 0;
          if (aValue === "N/A") return this.sortDirection === "asc" ? 1 : -1;
          if (bValue === "N/A") return this.sortDirection === "asc" ? -1 : 1;

          // Parse dates for comparison
          const aDate = this.parseDisplayDate(aValue);
          const bDate = this.parseDisplayDate(bValue);

          if (this.sortDirection === "asc") {
            return aDate - bDate;
          } else {
            return bDate - aDate;
          }
        });
      }

      return locations;
    },

    parseDisplayDate(dateString) {
      // Parse the display format back to Date object
      // Format is "DD MMM YYYY" (e.g., "15 Jan 2024")
      const parts = dateString.split(" ");
      const day = parseInt(parts[0]);
      const month = this.getMonthNumber(parts[1]);
      const year = parseInt(parts[2]);

      return new Date(year, month, day);
    },

    getMonthNumber(monthName) {
      const months = {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
      };
      return months[monthName] || 0;
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return "mdi-unfold-more-horizontal";
      }
      return this.sortDirection === "asc"
        ? "mdi-chevron-up"
        : "mdi-chevron-down";
    },
  },
  mounted() {
    this.fetchWorkspaceData();
    this.fetchLocations(); // Fetch locations on mount
  },
};
</script>

<style scoped>
/* Screenshot Header Styles */
.screenshot-header {
  margin: -20px -20px 1.5rem -20px; /* Negative margins to extend to container edges */
  position: relative;
  overflow: hidden;
}

.screenshot-header.hidden {
  display: none;
}

.maze-pattern {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  padding: 1.5rem 2rem;
  overflow: hidden;
}

.maze-pattern::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    /* Horizontal lines */ linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.1) 1px,
      transparent 1px
    ),
    /* Vertical lines */
      linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
    /* Diagonal cross pattern */
      linear-gradient(
        45deg,
        transparent 35%,
        rgba(255, 255, 255, 0.05) 35%,
        rgba(255, 255, 255, 0.05) 65%,
        transparent 65%
      ),
    /* Plus symbols */
      radial-gradient(
        circle at 25% 25%,
        rgba(255, 255, 255, 0.1) 2px,
        transparent 3px
      ),
    radial-gradient(
      circle at 75% 75%,
      rgba(255, 255, 255, 0.08) 2px,
      transparent 3px
    );
  background-size: 20px 20px, 20px 20px, 40px 40px, 80px 80px, 120px 120px;
  animation: mazeFloat 20s linear infinite;
}

@keyframes mazeFloat {
  0% {
    transform: translateX(0px);
  }
  100% {
    transform: translateX(40px);
  }
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.workspace-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.workspace-logo {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.workspace-name {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transform: translateY(-10px);
}

.date-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  position: relative;
  padding-right: 4rem;
  transform: translateY(-10px);
}

.date-text::after {
  content: "";
  position: absolute;
  right: -3.5rem;
  top: -20px;
  width: 60px;
  height: 80px;
  background-image: 
    /* Row 1 - Top bubbles */ radial-gradient(
      circle at 10px 8px,
      rgba(255, 255, 255, 0.4) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 25px 5px,
      rgba(255, 255, 255, 0.3) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 40px 10px,
      rgba(255, 255, 255, 0.35) 2.5px,
      transparent 3px
    ),
    radial-gradient(
      circle at 55px 7px,
      rgba(255, 255, 255, 0.25) 1px,
      transparent 2px
    ),
    /* Row 2 */
      radial-gradient(
        circle at 8px 20px,
        rgba(255, 255, 255, 0.3) 1.5px,
        transparent 2px
      ),
    radial-gradient(
      circle at 22px 18px,
      rgba(255, 255, 255, 0.4) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 35px 22px,
      rgba(255, 255, 255, 0.2) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 48px 19px,
      rgba(255, 255, 255, 0.35) 2px,
      transparent 3px
    ),
    /* Row 3 - Middle */
      radial-gradient(
        circle at 12px 32px,
        rgba(255, 255, 255, 0.25) 2px,
        transparent 3px
      ),
    radial-gradient(
      circle at 28px 30px,
      rgba(255, 255, 255, 0.2) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 42px 34px,
      rgba(255, 255, 255, 0.3) 2.5px,
      transparent 3px
    ),
    radial-gradient(
      circle at 52px 31px,
      rgba(255, 255, 255, 0.15) 1px,
      transparent 2px
    ),
    /* Row 4 */
      radial-gradient(
        circle at 6px 44px,
        rgba(255, 255, 255, 0.2) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 20px 42px,
      rgba(255, 255, 255, 0.25) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 36px 46px,
      rgba(255, 255, 255, 0.15) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 50px 43px,
      rgba(255, 255, 255, 0.2) 1.5px,
      transparent 2px
    ),
    /* Row 5 - Bottom */
      radial-gradient(
        circle at 15px 56px,
        rgba(255, 255, 255, 0.15) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 30px 54px,
      rgba(255, 255, 255, 0.1) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 45px 58px,
      rgba(255, 255, 255, 0.12) 1px,
      transparent 2px
    ),
    /* Additional scattered bubbles */
      radial-gradient(
        circle at 18px 68px,
        rgba(255, 255, 255, 0.08) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 38px 70px,
      rgba(255, 255, 255, 0.06) 1px,
      transparent 2px
    );
  background-size: 60px 80px;
}

.date-text::before {
  content: "";
  position: absolute;
  right: -3rem;
  top: -15px;
  width: 50px;
  height: 70px;
  background-image: 
    /* Left side additional bubbles */ radial-gradient(
      circle at 5px 12px,
      rgba(255, 255, 255, 0.2) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 15px 8px,
      rgba(255, 255, 255, 0.15) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 8px 25px,
      rgba(255, 255, 255, 0.18) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 18px 28px,
      rgba(255, 255, 255, 0.12) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 12px 40px,
      rgba(255, 255, 255, 0.1) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 22px 45px,
      rgba(255, 255, 255, 0.08) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 6px 55px,
      rgba(255, 255, 255, 0.06) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 16px 60px,
      rgba(255, 255, 255, 0.05) 1px,
      transparent 2px
    );
  background-size: 50px 70px;
}

/* Existing styles */
.container {
  margin: 0 20px; /* Add left and right margin */
}

.reviews-title {
  font-size: 24px; /* Adjust font size */
  font-weight: bold; /* Make the title bold */
  color: #1a1a1a; /* Change color to match the design */
  margin-bottom: 20px; /* Add space below the title */
  display: flex; /* Use flexbox for alignment */
  align-items: center; /* Center items vertically */
}

.back-arrow {
  cursor: pointer; /* Change cursor to pointer */
  margin-right: 10px; /* Add space between arrow and title */
  background-color: #f0f0f0; /* Light background color */
  border-radius: 50%; /* Make it circular */
  padding: 5px; /* Add some padding */
  font-size: 18px; /* Adjust font size */
  line-height: 1; /* Center the arrow vertically */
}

.filter-buttons {
  display: flex;
  margin-bottom: 20px;
  justify-content: space-between;
  width: 100%;
}

.filter-select {
  min-width: 250px !important;
  max-width: 400px !important;
}

.filter-select ::v-deep .v-select__selections {
  max-width: 100%;
  overflow: visible;
  white-space: nowrap;
}

.filter-select ::v-deep .v-select__selection {
  max-width: none;
  overflow: visible;
  text-overflow: unset;
}

.filter-buttons .v-select {
  flex-grow: 1;
}

.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 20px 0; /* Add some margin for spacing */
}

.locations-table {
  width: calc(100% - 20px); /* Adjust width to account for right margin */
  border-collapse: collapse;
  margin-top: 20px;
  margin-right: 20px; /* Add right margin */
}

.locations-table th,
.locations-table td {
  border: 1px solid #e5e7eb;
  padding: 10px;
  text-align: left;
}

.locations-table th {
  background-color: #007bff; /* Blue shade for the header */
  color: white; /* White text color for contrast */
}

.active {
  background-color: #007bff; /* Highlight color for active filter */
  color: white; /* Change text color for contrast */
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 0 20px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.items-per-page {
  min-width: 150px;
}

.items-per-page-select {
  width: 150px;
}

.pagination-button {
  cursor: pointer;
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background-color: white;
  color: #007bff;
  font-weight: bold;
  transition: all 0.2s ease;
}

.pagination-button:hover:not(:disabled) {
  background-color: #f3f4f6;
}

.pagination-button:disabled {
  color: #ccc;
  cursor: not-allowed;
  border-color: #e5e7eb;
}

/* Adjust the v-select styles */
.items-per-page ::v-deep .v-select__selections {
  min-height: 32px;
}

.items-per-page ::v-deep .v-input__slot {
  min-height: 32px;
}

.search-bar {
  margin-bottom: 20px; /* Add space below the search bar */
  margin-right: 20px; /* Add right margin */
}

.search-bar input {
  width: 100%; /* Full width for the input */
  padding: 10px; /* Add some padding */
  border: 1px solid #e5e7eb; /* Border style */
  border-radius: 24px; /* Rounded corners */
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sortable-header:hover {
  background-color: #0056b3 !important;
}

.sortable-header.sorted {
  background-color: #0056b3;
}

.sortable-header .v-icon {
  margin-left: 8px;
}
</style>
